import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { getOwners } from '@atlassian/search-client/owners';
import { smartsRecommendedUserQuery } from '@atlassian/search-client/user';
import { type User, usersQuery } from '@atlassian/search-client/user/user';
import { mergeAndDedupeOptions } from '@atlassian/search-common/merge-and-dedupe-options';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type {
	FetchedSelectFilterOption,
	SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys1P } from '../../../../../common/constants/product-keys';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';
import { getUrsProductKey } from '../get-urs-product-key';

const isFulfilled = <Value,>(
	result: PromiseSettledResult<Value>,
): result is PromiseFulfilledResult<Value> => result.status === 'fulfilled';

const isRejected = (result: PromiseSettledResult<unknown>): result is PromiseRejectedResult =>
	result.status === 'rejected';

const MAX_SMART_DEFAULT_OPTIONS = 5;
const MAX_SEARCHED_OPTIONS_RESULTS = 5;
// Each site still returns MAX_SEARCHED_OPTIONS_RESULTS, so five sites could otherwise surface 25.
const MAX_MULTI_SITE_SEARCHED_OPTIONS_RESULTS = 10;
// Same reasoning for the zero-query recommendations, which return MAX_SMART_DEFAULT_OPTIONS a site.
const MAX_MULTI_SITE_SMART_DEFAULT_OPTIONS = 10;

const toRecommendedUserOption = (config: CloudConfig, user: User): FetchedSelectFilterOption => ({
	trackingKey: user.accountId,
	value: user.accountId,
	label: user.name,
	avatarUrl: user.picture,
	queryParamValue: user.accountId,
	product: config.product,
});

// URS only accepts a scalar `siteId`, so multi-site search fans out one request per site.
const fetchOwnerOptionsForSite = async (
	query: string,
	config: CloudConfig,
	cloudId: string,
): Promise<FetchedSelectFilterOption[]> => {
	if (!config.intl) {
		return [];
	}

	const response = await getOwners({
		query,
		cloudId,
		userId: config.userId || '',
		productKey: getUrsProductKey(config.primaryProduct),
		maxNumberOfResults: MAX_SEARCHED_OPTIONS_RESULTS,
		intl: config.intl,
	});

	if (!response) {
		// TODO: log not found somewhere
		return [];
	}

	return response.map((owner) => ({
		trackingKey: owner.id,
		value: owner.id,
		label: owner.name,
		avatarUrl: owner.avatarUrl,
		queryParamValue: owner.id,
		product: config.product,
	}));
};

// `smarts.recommendedUser` takes a scalar `tenantId`, so recommendations fan out one query a site.
const fetchRecommendedOwnerOptionsForSite = async (
	config: CloudConfig,
	cloudId: string,
): Promise<FetchedSelectFilterOption[]> => {
	const response = await smartsRecommendedUserQuery({
		aggAbsoluteUrl: config.aggAbsoluteUrl,
		variables: {
			userId: config.userId || '',
			tenantId: cloudId,
			maxNumberOfResults: MAX_SMART_DEFAULT_OPTIONS,
		},
		// Attribution is per-site, so these headers cannot be hoisted out of the fan-out.
		headers: buildAtlAttributionHeaders({
			cloudId,
			activationId: config.activationId,
			product: config.primaryProduct,
		}),
	});

	const recommendedUsers = (response.data?.smarts.results ?? []).flatMap((recommendedUser) =>
		!!recommendedUser && !!recommendedUser.user
			? { id: recommendedUser.id, ...recommendedUser.user }
			: [],
	) satisfies User[];

	return recommendedUsers.map((user) => toRecommendedUserOption(config, user));
};

const loadRecommendedOwner = async (config: CloudConfig) => {
	// The current user leads the list and is not site-specific, so this stays a single query —
	// fanning it out would just return the same person once per site.
	const initialUsersQuery = usersQuery(
		[
			...(config.userId ? [config.userId] : []), // The current user should always be the first recommended user
		],
		buildAtlAttributionHeaders({
			cloudId: config.cloudId,
			activationId: config.activationId,
			product: config.primaryProduct,
		}),
	);

	const cloudIds = config.selectedCloudIds?.length
		? config.selectedCloudIds
		: [config.cloudId || ''];

	const [initialUsersResponse, settledPerSite] = await Promise.all([
		initialUsersQuery,
		Promise.allSettled(
			cloudIds.map((cloudId) => fetchRecommendedOwnerOptionsForSite(config, cloudId)),
		),
	]);

	const succeeded = settledPerSite.filter(isFulfilled);
	const [firstRejection] = settledPerSite.filter(isRejected);

	// Single-site kept rejecting the whole load when recommendations failed; preserve that, but do
	// not let one unhealthy site blank out the others.
	if (succeeded.length === 0 && firstRejection) {
		throw firstRejection.reason;
	}

	const currentUserOptions = (initialUsersResponse.data?.users ?? []).map((user) =>
		toRecommendedUserOption(config, user),
	);

	if (cloudIds.length === 1) {
		return [...currentUserOptions, ...succeeded[0].value];
	}

	// The current user leads the round-robin, so they stay first and are not duplicated by a site
	// that also recommends them.
	return mergeAndDedupeOptions([
		currentUserOptions,
		...succeeded.map((result) => result.value),
	]).slice(0, MAX_MULTI_SITE_SMART_DEFAULT_OPTIONS);
};

const productKeys1P: ProductKeys1P[] = [ProductKeys.Confluence, ProductKeys.Focus];

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('owner');

export const filter: Readonly<{
	id: 'owner';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'owner'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'owner'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'owner'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'owner',
	products: [...productKeys1P, ...products3PFromConfig],
	alwaysVisible: true,
	universal: false,
	queryParams: {
		key: 'owners',
		fetcher: async (queryParams, config) => {
			const headers = buildAtlAttributionHeaders({
				cloudId: config.cloudId,
				activationId: config.activationId,
				product: config.primaryProduct,
			});
			const initialUsersReponse = await usersQuery([...queryParams], headers);

			const allRecommendedUsers = initialUsersReponse.data?.users ?? ([] satisfies User[]);

			const recommendedUserOptions = allRecommendedUsers.map((user) => ({
				trackingKey: user.accountId,
				value: user.accountId,
				label: user.name,
				avatarUrl: user.picture,
				queryParamValue: user.accountId,
				product: config.product,
			}));

			return recommendedUserOptions;
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return loadRecommendedOwner(config);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			if (!config.intl) {
				return [];
			}

			const cloudIds = config.selectedCloudIds?.length
				? config.selectedCloudIds
				: [config.cloudId || ''];

			if (cloudIds.length === 1) {
				return fetchOwnerOptionsForSite(query, config, cloudIds[0]);
			}

			const settledPerSite = await Promise.allSettled(
				cloudIds.map((cloudId) => fetchOwnerOptionsForSite(query, config, cloudId)),
			);
			const succeeded = settledPerSite.filter(isFulfilled);
			const [firstRejection] = settledPerSite.filter(isRejected);

			// One unhealthy site should not blank out the whole filter; only fail if every site failed.
			if (succeeded.length === 0 && firstRejection) {
				throw firstRejection.reason;
			}

			return mergeAndDedupeOptions(succeeded.map((result) => result.value)).slice(
				0,
				MAX_MULTI_SITE_SEARCHED_OPTIONS_RESULTS,
			);
		},
	},
});
